// Companies > detail > Payouts: the bank check, the bank letter and the
// approval of the operator's payout account (Paystack plan 6.4 and 6.5,
// PR 7a). Any admin sees it; only a super admin can approve or reject (the
// server answers 403 to anyone else). A bank change (plan 6.10, #541) needs
// the call to the number on file at the last approval first. The approve
// click names the account and the letter this page showed, so the server
// refuses it (409) when either changed after the page loaded. When the call
// shows the change is not the operator's, a super admin refuses it with a
// note (#542): the approved account it replaced is the account on file again.

import React, { useState } from 'react';
import { ShieldCheck, FileText } from 'lucide-react';
import { AdminCompanyAPI, charterFileHref } from '../../services/api';
import { formatDateTime, ENV_LABEL, REVIEW_LABEL, OUTCOME_LABEL, LETTER_LABEL, yesNo, errorText } from './payoutLabels';

const CheckResult = ({ check }) => {
  if (!check) return <p className="text-sm text-gray-500">No bank check on record.</p>;
  return (
    <div className="text-sm text-gray-700 space-y-1">
      <p>
        <span className="font-medium text-gray-900">{OUTCOME_LABEL[check.outcome] || check.outcome}</span>
        <span className="text-gray-500"> ({ENV_LABEL[check.env] || ENV_LABEL.null}, {formatDateTime(check.checkedAt)})</span>
      </p>
      {check.message && <p className="text-gray-600">{check.message}</p>}
      {check.outcome !== 'not_supported' && check.outcome !== 'unavailable' && (
        <p className="text-gray-600">
          Verified: {yesNo(check.verified)}. Holder matches: {yesNo(check.accountHolderMatch)}. Account open: {yesNo(check.accountOpen)}. Accepts credits: {yesNo(check.acceptsCredits)}.
        </p>
      )}
    </div>
  );
};

/** A super admin refuses the change (#542): the approved account it replaced comes back. */
const RefuseChange = ({ change, busy, onRefuse }) => {
  const [why, setWhy] = useState('');
  const back = change.restores;
  if (!back) {
    return <p className="text-amber-800">This change was saved before Zuttle kept the approved account for a refusal, so it cannot be refused here. Phone the operator on the number on file, the number to phone above. The operator can save the approved account again.</p>;
  }
  const refuse = () => {
    if (!window.confirm(`Refuse this bank change? The ${back.bankName || 'bank'} account ending ${back.last4 || 'unknown'} becomes the approved account on file again, and the operator gets an email with your note.`)) return;
    onRefuse(why.trim());
  };
  return (
    <div className="border-t border-amber-200 pt-3 space-y-2">
      <p>
        If the call shows the operator did not ask for this change, refuse it. The {back.bankName || 'bank'} account ending {back.last4 || 'unknown'} becomes the approved account on file again.
      </p>
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-sm text-gray-700">
          <span className="block text-xs text-gray-500 mb-1">Why the change is refused (the operator sees this)</span>
          <input value={why} onChange={(e) => setWhy(e.target.value)} className="w-72 px-3 py-2 border border-gray-300 rounded-md text-sm" />
        </label>
        <button type="button" disabled={busy || why.trim().length < 3} onClick={refuse} className="px-4 py-2 border border-red-300 text-red-700 text-sm rounded-md bg-white hover:bg-red-50 disabled:opacity-50">
          Refuse the change
        </button>
      </div>
    </div>
  );
};

/** A bank change under review: who Paystack pays now, the new account, and the number to phone first. */
const ChangeBox = ({ change, account, isSuperAdmin, called, setCalled, busy, onRefuse, canRefuse }) => (
  <div className="border border-amber-200 bg-amber-50 rounded-md p-4 text-sm text-amber-900 space-y-2">
    <p className="font-medium">Bank change</p>
    <p>
      This replaces an approved account. Paystack pays the {change.paidAccount?.bankName || 'bank'} account ending {change.paidAccount?.last4 || 'unknown'} until you approve and the change reaches Paystack.
    </p>
    <p className="font-semibold">
      Confirm on the call: the new account is the {account?.bankName || 'bank'} account ending {account?.accountNumberLast4 || 'unknown'}.
    </p>
    <p>
      Before you approve, phone <span className="font-semibold">{change.callPhone || 'the operator'}</span>
      {change.callPhone ? ', the number on file at the last approval' : ' (no number was on file at the last approval)'}. Do not use the profile number for this call.
      {change.profilePhone && change.callPhone && change.profilePhone !== change.callPhone && ` The profile phone is now ${change.profilePhone}: it changed after the last approval.`}
    </p>
    {change.blockedUntil && (
      <p className="text-red-700">The login email changed less than 72 hours ago. You can approve from {formatDateTime(change.blockedUntil)}.</p>
    )}
    {isSuperAdmin && (
      <label className="flex items-center gap-2">
        <input type="checkbox" checked={called} onChange={(e) => setCalled(e.target.checked)} />
        I phoned {change.callPhone || 'the operator'} and the operator confirmed this change
      </label>
    )}
    {isSuperAdmin && canRefuse && <RefuseChange change={change} busy={busy} onRefuse={onRefuse} />}
  </div>
);

/** The last refused change of the approved account (#542). */
const RefusalLine = ({ refusal }) => (
  <p className="text-sm text-gray-700">
    Change refused {formatDateTime(refusal.at)}: the {refusal.bankName || 'bank'} account ending {refusal.last4 || 'unknown'}. Note: {refusal.note}
  </p>
);

const PayoutReview = ({ companyId, data, isSuperAdmin, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [note, setNote] = useState('');
  const [called, setCalled] = useState(false);
  const review = data.review;
  const letter = data.letter;
  if (!review) return null;
  const change = review.status !== 'approved' ? data.change : null;

  const run = async (call) => {
    setBusy(true);
    setError(null);
    try {
      const res = await call();
      onChanged(res.data.data);
      setNote('');
      setCalled(false);
    } catch (err) {
      setError(errorText(err, 'That did not go through'));
      await onChanged(null);
    } finally {
      setBusy(false);
    }
  };

  const letterWaiting = letter?.status === 'pending';
  const approvalEnv = data.server?.approvalEnv;
  const envBlock = approvalEnv ? data.subaccounts?.[approvalEnv] : null;
  const canApprove = (review.status === 'checked'
    || (review.status === 'manual_review' && ['pending', 'verified'].includes(letter?.status)))
    && !(change && change.blockedUntil);
  // The full number is gone once the live subaccount exists (a live code is
  // stored). Then only a block that already has its subaccount (a split-only
  // retry) can be created; the server refuses any other.
  const live = data.subaccounts?.live;
  const fullNumberGone = Boolean(live?.subaccountCode) || ['created', 'verified'].includes(live?.status);
  const canRetryCreate = review.status === 'approved' && envBlock && envBlock.status === 'none'
    && (Boolean(envBlock.subaccountCode) || !fullNumberGone);
  const withLetter = review.status === 'manual_review' && letterWaiting;
  let approveLabel = withLetter ? 'Accept the letter and approve' : 'Approve account';
  if (change) approveLabel = withLetter ? 'Accept the letter and approve the change' : 'Approve the change';
  if (canRetryCreate) approveLabel = `Create the ${ENV_LABEL[approvalEnv] || approvalEnv} subaccount`;

  const approve = () => {
    if (!window.confirm(canRetryCreate
      ? 'Send the subaccount create to Paystack again?'
      : change
        ? 'Approve this bank change? Zuttle sends the new account to Paystack, which holds payouts until it verifies the account again.'
        : 'Approve this payout account? Zuttle pays this operator into it.')) return;
    if (canRetryCreate) {
      run(() => AdminCompanyAPI.approveBankDetails(companyId));
      return;
    }
    // What this page showed: the server refuses the approval if either changed.
    const shown = { submittedAt: review.submittedAt, ...(letter ? { letterId: letter.id } : {}) };
    run(() => AdminCompanyAPI.approveBankDetails(companyId, change ? { ...shown, callConfirmed: true } : shown));
  };
  const reject = () => run(() => AdminCompanyAPI.rejectBankLetter(companyId, note));
  // Names the change this page showed: the server refuses it (409) if the operator saved another since.
  const refuseChange = (why) => run(() => AdminCompanyAPI.refuseBankChange(companyId, { submittedAt: review.submittedAt, note: why }));

  return (
    <div className="mt-6 border-t border-gray-100 pt-4 space-y-4">
      <div className="flex items-center gap-2">
        <ShieldCheck size={16} className="text-emerald-600" />
        <h3 className="text-sm font-semibold text-gray-700">Review</h3>
        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
          {REVIEW_LABEL[review.status] || review.status}
        </span>
        {review.approvedAt && <span className="text-xs text-gray-500">{formatDateTime(review.approvedAt)}</span>}
      </div>

      {/* No refusal while the save's bank check runs: the server answers 409 CHECK_RUNNING. */}
      {change && <ChangeBox change={change} account={data.bankDetails} isSuperAdmin={isSuperAdmin} called={called} setCalled={setCalled} busy={busy} onRefuse={refuseChange} canRefuse={review.status !== 'validating'} />}
      {review.status === 'approved' && review.refusal && <RefusalLine refusal={review.refusal} />}

      <div>
        <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Bank check</p>
        <CheckResult check={review.validation} />
      </div>

      <div>
        <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Bank letter</p>
        {letter ? (
          <div className="text-sm text-gray-700 flex flex-wrap items-center gap-3">
            <FileText size={16} className="text-gray-500" />
            <span className="font-medium text-gray-900">{LETTER_LABEL[letter.status] || letter.status}</span>
            <span className="text-gray-500">uploaded {formatDateTime(letter.uploadedAt)}</span>
            {letter.fileUrl
              ? <a href={charterFileHref(letter.fileUrl)} target="_blank" rel="noopener noreferrer" className="text-emerald-700 underline">Open letter</a>
              : <span className="text-gray-500">File deleted</span>}
            {letter.note && <span className="text-gray-600">Note: {letter.note}</span>}
          </div>
        ) : (
          <p className="text-sm text-gray-500">
            {data.letterNeeded ? 'Waiting for the operator to upload a bank letter.' : 'No letter needed.'}
          </p>
        )}
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      {isSuperAdmin ? (
        <div className="flex flex-wrap items-end gap-3">
          {(canApprove || canRetryCreate) && (
            <button type="button" disabled={busy || (Boolean(change) && !called && !canRetryCreate)} onClick={approve} className="px-4 py-2 bg-emerald-600 text-white text-sm rounded-md hover:bg-emerald-700 disabled:opacity-50">
              {approveLabel}
            </button>
          )}
          {letterWaiting && (
            <div className="flex flex-wrap items-end gap-2">
              <label className="text-sm text-gray-700">
                <span className="block text-xs text-gray-500 mb-1">Why the letter is rejected (the operator sees this)</span>
                <input value={note} onChange={(e) => setNote(e.target.value)} className="w-72 px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </label>
              <button type="button" disabled={busy || note.trim().length < 3} onClick={reject} className="px-4 py-2 border border-red-300 text-red-700 text-sm rounded-md hover:bg-red-50 disabled:opacity-50">
                Reject letter
              </button>
            </div>
          )}
        </div>
      ) : (
        <p className="text-sm text-gray-500">Only a super admin can approve, refuse a change, reject a letter or create subaccounts.</p>
      )}
    </div>
  );
};

export default PayoutReview;
